import { KeenSliderInstance } from "keen-slider";
import { ref, watch } from "vue";

const continualScrollingEasing = (t: number) => t;

/**
 * For auto swipe your slides.
 * This plugin modified options -> It have to be used at first place, if you need to use others plugins.
 * @param timeout is time to swipe next slide.
 * @param continualScrolling means the slider will be move constantly.
 */
export default function useKeenAutoplayPlugin(timeout = 5000, continualScrolling = false) {
    const stopKeenAutoplay = ref(false);

    let timeoutHandler: ReturnType<typeof setTimeout>;
    let isAnimationStoppedInternal = false;

    const keenAutoplayPlugin = (slider: KeenSliderInstance) => {
        const slidesLength = slider.slides.length;

        const setOptions = () => {
            slider.options.loop = true;

            if (continualScrolling) {
                slider.options.mode = "free";
                slider.options.rubberband = false;

                // fix "abs" bug with loop true - its only working for continual scroll :(
                if (typeof slider.options.slides == "object") {
                    slider.options.slides.origin = "center";
                }

                if (timeout < 2000) {
                    slider.options.renderMode = "performance";
                }
            }
        };

        /* ************************ CONTINUAL FN ****************** */

        const oneRoundAnimation = () => {
            if (isAnimationStoppedInternal || stopKeenAutoplay.value) return;

            slider.moveToIdx(slider.track.details.abs + slidesLength, true, {
                duration: timeout * slidesLength,
                easing: continualScrollingEasing,
            });
        };

        const stopOneRoundAnimation = () => {
            slider.animator.stop();
        };

        /* ************************ STEP FN  ***************************** */

        const nextSlideTimeouted = () => {
            clearTimeout(timeoutHandler);
            if (isAnimationStoppedInternal || stopKeenAutoplay.value) return;

            timeoutHandler = setTimeout(() => {
                slider.next();
            }, timeout);
        };

        const stopNextSlideTimeouted = () => {
            clearTimeout(timeoutHandler);
        };

        /* ****************************** SETUP *********************** */

        const startKeenProcess = continualScrolling ? oneRoundAnimation : nextSlideTimeouted;
        const stopKeenProcess = continualScrolling ? stopOneRoundAnimation : stopNextSlideTimeouted;

        slider.on("created", () => {
            setOptions();
            slider.update();

            slider.container.addEventListener("mouseenter", () => {
                isAnimationStoppedInternal = true;
                stopKeenProcess();
            });
            slider.container.addEventListener("mouseleave", () => {
                isAnimationStoppedInternal = false;
                startKeenProcess();
            });

            watch(
                stopKeenAutoplay,
                (newStopKeenAutoplay) => {
                    if (newStopKeenAutoplay) {
                        stopKeenProcess();
                    } else {
                        startKeenProcess();
                    }
                },
                { immediate: true }
            );
        });

        slider.on("animationEnded", startKeenProcess);
        slider.on("updated", startKeenProcess);
        slider.on("optionsChanged", () => {
            // breakpoints are removing auto-added options
            setOptions();
            slider.update();
        });

        slider.on("destroyed", () => {
            stopNextSlideTimeouted();
        });
    };

    return { keenAutoplayPlugin, stopKeenAutoplay };
}
